import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Box, Button, Alert, Card, CardContent, Chip, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import TruckAnimation3D from '../components/TruckAnimation3D';
import { productService, categoryService } from '../services/api';
import { FaBolt, FaShieldAlt, FaTruck, FaCheckCircle, FaStar, FaAward, FaQuoteLeft, FaEnvelope, FaSearch, FaBox, FaTh, FaLightbulb, FaTimes, FaShoppingCart, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import CategoryShowcase from '../components/CategoryShowcase';
import BrandsCarousel from '../components/BrandsCarousel';

// Values data
const valuesData = [
  {
    id: 0,
    icon: <FaBolt size={32} />,
    title: 'Efficacité Énergétique',
    description: 'Consommation réduite de 80% par rapport aux technologies traditionnelles',
    color: '#fbbf24',
  },
  {
    id: 1,
    icon: <FaLightbulb size={32} />,
    title: 'Performance Lumineuse',
    description: 'Éclairage optimal avec une durée de vie exceptionnelle de 50 000 heures',
    color: '#f59e0b',
  },
  {
    id: 2,
    icon: <FaTruck size={32} />,
    title: 'Polyvalence Véhicule',
    description: 'Compatible avec tous types de véhicules professionnels et d\'urgence',
    color: '#00ACEA',
  },
  {
    id: 3,
    icon: <FaStar size={32} />,
    title: 'Sécurité Renforcée',
    description: 'Visibilité maximale pour garantir la sécurité sur toutes les routes',
    color: '#4DB9FF',
  },
];

const Home = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [imageIndexes, setImageIndexes] = useState({});
  const [activeValue, setActiveValue] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await productService.getAll();
        const allProducts = Array.isArray(response.data) ? response.data : [];
        setProducts(allProducts);

        // Randomize order and take first 3 consistently
        const shuffled = [...allProducts];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        setSelectedProducts(shuffled.slice(0, 3));
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await categoryService.getAll();
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchProducts();
    fetchCategories();
  }, []);

  if (loading) return <Loading />;

  const resolveImageUrl = (product) => {
    const img = product.image || (product.images && product.images[0]);
    if (!img) return 'https://via.placeholder.com/400x400?text=Produit';
    if (/^https?:\/\//i.test(img) || /^data:/i.test(img)) return img;
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    return `${base.replace(/\/$/, '')}/${String(img).replace(/^\//, '')}`;
  };
  
  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', overflow: 'hidden', width: '100%', maxWidth: '100vw' }}>
      {/* Hero Section - Minimal Professional (restored from old version) */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          maxWidth: '100vw',
          minHeight: { xs: '90vh', md: '85vh' },
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(155deg, #00324C 0%, #005F8A 45%, #0090D1 100%)',
          py: { xs: 6, md: 8 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1600&q=80)',
            backgroundSize: { xs: 'cover', md: 'cover' },
            backgroundPosition: { xs: '20% center', md: 'center right' },
            opacity: 0.12,
            zIndex: 0,
          },
          '@keyframes fadeInUp': {
            from: { opacity: 0, transform: 'translateY(30px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          '@keyframes slideInLeft': {
            from: { opacity: 0, transform: 'translateX(-40px)' },
            to: { opacity: 1, transform: 'translateX(0)' },
          },
          '@keyframes scaleIn': {
            from: { opacity: 0, transform: 'scale(0.9)' },
            to: { opacity: 1, transform: 'scale(1)' },
          },
        }}
      >
        {/* Geometric Patterns (circle removed per request) */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '15%',
            right: '8%',
            width: '200px',
            height: '200px',
            border: '2px solid rgba(14, 165, 233, 0.15)',
            borderRadius: '30px',
            transform: 'rotate(45deg)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" justifyContent="space-between" direction="row">
            {/* Truck visual now on the left */}
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'flex-start', position: 'relative' }}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  height: 500,
                  animation: 'scaleIn 1s ease-out 0.3s both',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <TruckAnimation3D />
              </Box>
            </Grid>
            {/* Text/content moved to the right */}
            <Grid item xs={12} md={7} sx={{ display: 'flex', justifyContent: { md: 'flex-end' } }}>
              <Box sx={{ animation: 'slideInLeft 0.8s ease-out', textAlign: { xs: 'left', md: 'right' }, ml: { md: 'auto' }, pr: { md: 2, lg: 4 } }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem', lg: '4.5rem' },
                    fontWeight: 900,
                    color: 'white',
                    lineHeight: 1.1,
                    mb: 2.5,
                    letterSpacing: '-0.03em',
                  }}
                >
                  Équipement de
                  <br />
                  Véhicules lourds
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Professionnels
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: { xs: '1.05rem', md: '1.15rem' },
                    fontWeight: 500,
                    lineHeight: 1.5,
                    mb: 4,
                    maxWidth: '560px',
                  }}
                >
                  Votre partenaire en pièces et éclairage pour poids lourds.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 5, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/catalogue')}
                    startIcon={<FaSearch />}
                    sx={{
                      background: 'linear-gradient(135deg,#F5A000 0%,#C87700 100%)',
                      color: '#000',
                      px: 4,
                      py: 1.75,
                      fontSize: '1.063rem',
                      fontWeight: 700,
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(245,160,0,0.35)',
                      '&:hover': {
                        background: 'linear-gradient(135deg,#C87700 0%,#B86A00 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 32px rgba(245,160,0,0.45)',
                      },
                    }}
                  >
                    Voir le catalogue
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/contact')}
                    startIcon={<FaEnvelope />}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      px: 4,
                      py: 1.75,
                      fontSize: '1.063rem',
                      fontWeight: 700,
                      borderRadius: '12px',
                      borderWidth: '2px',
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        borderColor: 'white',
                        borderWidth: '2px',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    Contact
                  </Button>
                </Box>

                <Grid container spacing={2} sx={{ maxWidth: 460, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  {[
                    { value: '100+', label: 'Produits' },
                    { value: '98%', label: 'Satisfaction' },
                  ].map((stat, idx) => (
                    <Grid item xs={4} key={idx}>
                      <Box>
                        <Typography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, color: '#fbbf24', lineHeight: 1 }}>{stat.value}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{stat.label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Truck Brands Marquee */}
      <Box sx={{ bgcolor: '#ffffff' }}>
        <BrandsCarousel />
      </Box>

      {/* Featured Products Section - Clean Modern Design (centered vertically) */}
      <Box sx={{ bgcolor: '#f8fafc', py: 0, minHeight: { xs: '68vh', md: '72vh' }, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 8, md: 10 }, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              variant="overline"
              sx={{
                color: '#f59e0b',
                fontWeight: 800,
                fontSize: '0.875rem',
                letterSpacing: '2px',
                mb: 1,
                display: 'block',
              }}
            >
              PRODUITS VEDETTES
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 900,
                color: '#0c4a6e',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Nos Meilleurs Produits
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                color: '#64748b',
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              Découvrez nos produits les plus populaires, plébiscités par les professionnels
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  border: '4px solid #e2e8f0',
                  borderTopColor: '#0077B8',
                  animation: 'spin 0.8s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </Box>
          ) : products.length === 0 ? (
            <Alert 
              severity="info"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                borderRadius: '16px',
                '& .MuiAlert-icon': {
                  fontSize: '2rem',
                },
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Catalogue en préparation
              </Typography>
              <Typography>
                Nos produits seront bientôt disponibles. Contactez-nous pour plus d'informations.
              </Typography>
            </Alert>
          ) : (
            <>
              <Grid 
                container 
                spacing={{ xs: 3, md: 4 }} 
                justifyContent="center"
                alignItems="flex-start"
                sx={{
                  '& > .MuiGrid-item': {
                    display: 'flex',
                    alignItems: 'stretch',
                  }
                }}
              >
                {selectedProducts.map((product) => {
                const images = product.images && product.images.length > 0 
                  ? product.images 
                  : (product.image ? [product.image] : ['https://via.placeholder.com/400x300?text=Produit']);
                const currentIndex = imageIndexes[product.id] || 0;
                const hasMultipleImages = images.length > 1;

                const handlePrevImage = (e) => {
                  e.stopPropagation();
                  setImageIndexes(prev => ({
                    ...prev,
                    [product.id]: currentIndex === 0 ? images.length - 1 : currentIndex - 1
                  }));
                };

                const handleNextImage = (e) => {
                  e.stopPropagation();
                  setImageIndexes(prev => ({
                    ...prev,
                    [product.id]: currentIndex === images.length - 1 ? 0 : currentIndex + 1
                  }));
                };

                return (
                  <Grid item xs={12} sm={6} md={4} key={product.id}>
                    <Card
                      sx={{
                        width: '100%',
                        height: '400px', // reduced overall card height
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        position: 'relative',
                        bgcolor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 16px 48px rgba(0, 119, 184, 0.2)',
                          '& .product-image': {
                            transform: 'scale(1.03)',
                          },
                          '& .nav-arrow': {
                            opacity: 1,
                          },
                          '& .view-details-btn': {
                            bgcolor: '#0077B8',
                            color: 'white',
                          }
                        },
                      }}
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      {/* Badge Catégorie */}
                      <Chip
                        label={product.categorie_titre || 'Produit'}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          bgcolor: '#0077B8',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.688rem',
                          zIndex: 3,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 8px rgba(0, 119, 184, 0.3)',
                        }}
                      />

                      {/* Container Image */}
                      <Box
                        sx={{
                          position: 'relative',
                          height: '220px', // reduced image container height
                          width: '100%',
                          bgcolor: '#f8fafc',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 2,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          className="product-image"
                          component="img"
                          src={images[currentIndex]}
                          alt={product.titre}
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/400x400?text=Produit';
                          }}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover', // crop overflowing wide/tall images to keep card size constant
                            objectPosition: 'center',
                            display: 'block',
                            transition: 'transform 0.4s ease',
                          }}
                        />

                        {/* Flèches de navigation */}
                        {hasMultipleImages && (
                          <>
                            <IconButton
                              className="nav-arrow"
                              onClick={handlePrevImage}
                              sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                width: 36,
                                height: 36,
                                opacity: 0,
                                transition: 'all 0.3s ease',
                                zIndex: 2,
                                '&:hover': {
                                  bgcolor: '#0077B8',
                                  color: 'white',
                                  transform: 'translateY(-50%) scale(1.1)',
                                },
                              }}
                            >
                              <FaChevronLeft size={16} />
                            </IconButton>
                            <IconButton
                              className="nav-arrow"
                              onClick={handleNextImage}
                              sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                width: 36,
                                height: 36,
                                opacity: 0,
                                transition: 'all 0.3s ease',
                                zIndex: 2,
                                '&:hover': {
                                  bgcolor: '#0077B8',
                                  color: 'white',
                                  transform: 'translateY(-50%) scale(1.1)',
                                },
                              }}
                            >
                              <FaChevronRight size={16} />
                            </IconButton>
                          </>
                        )}

                        {/* Indicateur images */}
                        {hasMultipleImages && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 12,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              bgcolor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {currentIndex + 1}/{images.length}
                          </Box>
                        )}
                      </Box>

                      {/* Content */}
                      <CardContent 
                        sx={{ 
                          p: 2, // slightly tighter padding
                          flexGrow: 1, 
                          display: 'flex', 
                          flexDirection: 'column',
                          justifyContent: 'flex-start', // avoid stretching space between title and price
                          alignItems: 'center',
                          textAlign: 'center',
                          gap: 1.25,
                        }}
                      >
                        {/* Titre */}
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.938rem',
                            color: '#0c4a6e',
                            mb: 0.75, // reduced space below title
                            lineHeight: 1.4,
                            height: '44px',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {product.titre}
                        </Typography>

                        {/* Prix */}
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#64748b', 
                              fontSize: '0.813rem', 
                              fontWeight: 600,
                              mb: 1.25, // reduced bottom margin of price block
                            }}
                          >
                            Prix sur demande
                          </Typography>

                          {/* Bouton d'action */}
                          <Button
                            className="view-details-btn"
                            variant="outlined"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/products/${product.id}`);
                            }}
                            startIcon={<FaShoppingCart />}
                            sx={{
                              borderColor: '#0077B8',
                              color: '#0077B8',
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              py: 1.25,
                              borderRadius: '8px',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: '#0077B8',
                                bgcolor: '#0077B8',
                                color: 'white',
                              },
                            }}
                          >
                            Voir détails
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/catalogue')}
                  endIcon={<FaSearch />}
                  sx={{
                    borderColor: '#0077B8',
                    color: '#0077B8',
                    px: 5,
                    py: 1.75,
                    fontSize: '1.063rem',
                    fontWeight: 700,
                    borderRadius: '12px',
                    borderWidth: '2px',
                    '&:hover': {
                      borderWidth: '2px',
                      bgcolor: '#0077B8',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(0, 119, 184, 0.25)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Voir tous les produits
                </Button>
              </Box>
            </>
          )}
        </Container>
      </Box>

      {/* Notre Localisation */}
        <Box sx={{ bgcolor: '#ffffff', py: { xs: 8, md: 12 } }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="overline"
                sx={{
                  color: '#00ACEA',
                  fontWeight: 800,
                  fontSize: '0.875rem',
                  letterSpacing: '2px',
                  mb: 1,
                  display: 'block',
                }}
              >
                NOTRE LOCALISATION
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.25rem' },
                  fontWeight: 900,
                  color: '#0c4a6e',
                  mb: 2,
                  letterSpacing: '-0.02em',
                }}
              >
                Où nous trouver
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.063rem' },
                  color: '#64748b',
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: 1.7,
                  mb: 4,
                }}
              >
                Retrouvez notre emplacement exact sur Google Maps et contactez-nous pour une visite.
              </Typography>
            </Box>

            {/* Map embed */}
            <Box
              sx={{
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid #e2e8f0',
              }}
            >
              <Box
                component="iframe"
                title="Notre localisation"
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3324.258376147363!2d-7.480193924303964!3d33.57264017334138!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMzPCsDM0JzIxLjUiTiA3wrAyOCczOS40Ilc!5e0!3m2!1sfr!2sma!4v1764444223074!5m2!1sfr!2sma"
                sx={{
                  width: '100%',
                  height: { xs: 360, md: 480 },
                  border: 0,
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.open('https://maps.app.goo.gl/FSqN2Ph9pMyZ5xLX6', '_blank')}
                sx={{
                  bgcolor: '#0077B8',
                  fontWeight: 700,
                  px: 3,
                }}
              >
                Ouvrir dans Google Maps
              </Button>
            </Box>
          </Container>
        </Box>

      {/* Category Showcase (moved after localisation) - hidden on mobile */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, bgcolor: '#0c4a6e', pt: { xs: 8, md: 10 }, pb: { xs: 18, md: 24 }, position: 'relative', overflow: 'visible' }}>
          <Container maxWidth="xl" sx={{ mb: 4 }}>
            <CategoryShowcase categories={categoriesLoading ? [] : categories} />
          </Container>
        </Box>

      {/* Our Core Values - Compact Professional Design */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #0077B8 0%, #005a8f 100%)',
          py: { xs: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          {/* Header Compact */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '3px',
                color: 'rgba(255, 255, 255, 0.8)',
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              Nos 4 Valeurs Fondamentales
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontWeight: 800,
                color: 'white',
                lineHeight: 1.3,
              }}
            >
              Excellence & Performance
            </Typography>
          </Box>

          {/* Values Grid - Compact 4 Cards (centered) */}
          <Grid container spacing={3} justifyContent="center" sx={{ mb: 4 }}>
            {valuesData.map((value, index) => (
              <Grid item xs={6} md={3} key={value.id}>
                <Box
                  onClick={() => setActiveValue(index)}
                  sx={{
                    cursor: 'pointer',
                    p: 3,
                    height: '100%',
                    background: activeValue === index
                      ? `linear-gradient(135deg, ${value.color}22, ${value.color}11)`
                      : 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    border: activeValue === index 
                      ? `2px solid ${value.color}`
                      : '2px solid rgba(255, 255, 255, 0.15)',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      background: `linear-gradient(135deg, ${value.color}22, ${value.color}11)`,
                      borderColor: value.color,
                      boxShadow: `0 8px 24px ${value.color}44`,
                    },
                  }}
                >
                  <Box 
                    sx={{ 
                      fontSize: { xs: 32, md: 40 },
                      color: activeValue === index ? value.color : 'white',
                      mb: 1.5,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      fontWeight: 700,
                      color: 'white',
                      lineHeight: 1.3,
                    }}
                  >
                    {value.title}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Active Value Description - Compact */}
          <Box
            sx={{
              p: 4,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              border: `2px solid ${valuesData[activeValue].color}`,
              textAlign: 'center',
              boxShadow: `0 8px 32px ${valuesData[activeValue].color}44`,
              transition: 'all 0.4s ease',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.938rem', md: '1.063rem' },
                color: 'white',
                lineHeight: 1.7,
                fontWeight: 500,
                mb: 2,
              }}
            >
              {valuesData[activeValue].description}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.813rem', md: '0.938rem' },
                color: 'rgba(255, 255, 255, 0.85)',
                lineHeight: 1.6,
                '& strong': {
                  color: '#fbbf24',
                  fontWeight: 700,
                },
              }}
            >
              <strong>Économie d'énergie de 80%</strong> • Technologies LED professionnelles • Durabilité garantie
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section removed as requested */}
    </Box>
  );
};

export default Home;
